// frontend/src/components/process/ProcessFlowVisualizer.js
import React from 'react';
import { 
    ExclamationTriangleIcon, 
    SparklesIcon, 
    ClockIcon, 
    UserIcon, 
    CogIcon 
} from '@heroicons/react/24/outline';

const ProcessFlowVisualizer = ({ steps = [], bottlenecks = [], aiOpportunities = [] }) => {
    if (!steps || steps.length === 0) {
        return (
            <div className="p-6 text-center text-gray-500 bg-gray-50 border border-dashed rounded-lg">
                No process steps defined yet.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Step Flow List */}
            <div className="relative border-l-2 border-black pl-6 space-y-6">
                {steps.map((step, idx) => {
                    const stepName = step.name || `Step ${idx + 1}`;
                    const stepDesc = step.description || '';
                    const role = step.role || step.roles || '';
                    const system = step.system || step.systems || '';
                    const duration = step.duration || step.time_estimate || '';
                    const isBottleneck = step.is_bottleneck || false;
                    const aiIdea = step.ai_opportunity || '';

                    return (
                        <div key={idx} className="relative group">
                            {/* Circle bullet */}
                            <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-black border-2 border-white ring-2 ring-black" />

                            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow transition-shadow">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2">
                                        <span className="font-bold text-sm bg-gray-100 px-2 py-0.5 rounded text-gray-800">
                                            #{idx + 1}
                                        </span>
                                        <h4 className="font-semibold text-gray-900 text-base">{stepName}</h4>
                                    </div>
                                    {isBottleneck && (
                                        <span className="inline-flex items-center space-x-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                                            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                                            <span>Bottleneck</span>
                                        </span>
                                    )}
                                </div>

                                {stepDesc && (
                                    <p className="text-sm text-gray-600 mt-2">{stepDesc}</p>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                                    {role && (
                                        <div className="flex items-center space-x-1">
                                            <UserIcon className="w-4 h-4 text-gray-400" />
                                            <span>Role: <strong className="text-gray-700">{Array.isArray(role) ? role.join(', ') : role}</strong></span>
                                        </div>
                                    )}
                                    {system && (
                                        <div className="flex items-center space-x-1">
                                            <CogIcon className="w-4 h-4 text-gray-400" />
                                            <span>System: <strong className="text-gray-700">{Array.isArray(system) ? system.join(', ') : system}</strong></span>
                                        </div>
                                    )}
                                    {duration && (
                                        <div className="flex items-center space-x-1">
                                            <ClockIcon className="w-4 h-4 text-gray-400" />
                                            <span>Duration: <strong className="text-gray-700">{duration}</strong></span>
                                        </div>
                                    )}
                                </div>

                                {aiIdea && (
                                    <div className="mt-3 p-2.5 bg-purple-50 border border-purple-200 rounded-md text-xs flex items-start space-x-2 text-purple-900">
                                        <SparklesIcon className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                                        <div>
                                            <span className="font-semibold text-purple-900">AI Opportunity: </span>
                                            <span>{aiIdea}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* General Bottlenecks & AI Summary */}
            {(bottlenecks.length > 0 || aiOpportunities.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    {bottlenecks.length > 0 && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-red-900 flex items-center space-x-1.5 mb-2">
                                <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                                <span>Identified Bottlenecks</span>
                            </h5>
                            <ul className="list-disc list-inside text-xs text-red-800 space-y-1">
                                {(Array.isArray(bottlenecks) ? bottlenecks : [bottlenecks]).map((b, i) => (
                                    <li key={i}>{typeof b === 'string' ? b : b.description || b.name}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {aiOpportunities.length > 0 && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-purple-900 flex items-center space-x-1.5 mb-2">
                                <SparklesIcon className="w-4 h-4 text-purple-600" />
                                <span>Process AI Opportunities</span>
                            </h5>
                            <ul className="list-disc list-inside text-xs text-purple-800 space-y-1">
                                {(Array.isArray(aiOpportunities) ? aiOpportunities : [aiOpportunities]).map((ai, i) => (
                                    <li key={i}>{typeof ai === 'string' ? ai : ai.title || ai.description}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProcessFlowVisualizer;
